import { useSetAtom } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  AppointmentInfoResult,
  ContractRuleStatus,
  Modal,
  PublicNode,
} from '../../../__generated__/api'
import { AppointmentLengthOption } from '../../../common/components/AppointmentLengthSelect/AppointmentLengthSelect'
import { modalTriggererAtom } from '../../../state/appointmentOptionsSelect/atoms'
import { AppointmentOptions } from '../../appointmentOptionsSelect/components/AppointmentOptionsSelectModal'
import {
  AppointmentNoteConfig,
  AppointmentNoteType,
} from '../../search/components/AppointmentNoteModal'
import { findDefaultModalByOhcStatus } from '../../search/utils/modal.utils'

type ModalPhase = 'loading' | 'noteModal' | 'optionsModal' | 'navigating'

const deriveOhcStatus = (info: AppointmentInfoResult): ContractRuleStatus | undefined => {
  if (info.isClinicAppointmentPermitted && info.ohcClinicAppointmentRule) {
    return info.ohcClinicAppointmentRule
  }
  if (info.isVideoAppointmentPermitted && info.ohcVideoAppointmentRule) {
    return info.ohcVideoAppointmentRule
  }
  if (info.isPhoneAppointmentPermitted && info.ohcPhoneAppointmentRule) {
    return info.ohcPhoneAppointmentRule
  }
  return (
    info.ohcClinicAppointmentRule ?? info.ohcVideoAppointmentRule ?? info.ohcPhoneAppointmentRule
  )
}

interface Params {
  appointmentInfo: AppointmentInfoResult | null
  node: PublicNode | null
  nodePending: boolean
  nodeId: string | null
  isOhc: boolean
  modals: Modal[]
  modalsLoaded: boolean
  shouldShowOptionsModal: boolean
  lengthOptions: AppointmentLengthOption[] | null
  practitionerDetailsReady: boolean
  optionsModalDataFailed: boolean
  onDigitalClinic: (queue: string) => void
  navigateToAppointment: (options?: AppointmentOptions) => void
  buildBackPath: () => string
}

export const usePreselectionModals = ({
  appointmentInfo,
  node,
  nodePending,
  nodeId,
  isOhc,
  modals,
  modalsLoaded,
  shouldShowOptionsModal,
  lengthOptions,
  practitionerDetailsReady,
  optionsModalDataFailed,
  onDigitalClinic,
  navigateToAppointment,
  buildBackPath,
}: Params) => {
  const history = useHistory()
  const setModalTriggerer = useSetAtom(modalTriggererAtom)

  const [modalPhase, setModalPhase] = useState<ModalPhase>('loading')
  const [noteModalConfig, setNoteModalConfig] = useState<AppointmentNoteConfig | null>(null)

  const makeNoteConfig = useCallback(
    (modal: Modal): AppointmentNoteConfig => ({
      type: AppointmentNoteType.CUSTOM,
      modal,
      onContinue: () => {},
      onClose: () => {},
      onDigitalClinic,
    }),
    [onDigitalClinic]
  )

  useEffect(() => {
    if (modalPhase !== 'loading' || !appointmentInfo) return

    if (nodeId && nodePending) return
    if (!modalsLoaded) return

    // Mirrors getAppointmentNoteConfig logic from the search flow
    if (node?.modalId) {
      const modal = modals.find((m) => m.id === node.modalId)
      if (modal) {
        setNoteModalConfig(makeNoteConfig(modal))
        setModalPhase('noteModal')
        return
      }
    }

    if (isOhc) {
      const ohcStatus = deriveOhcStatus(appointmentInfo)
      const ohcModal = findDefaultModalByOhcStatus(ohcStatus, modals)
      if (ohcModal) {
        setNoteModalConfig(makeNoteConfig(ohcModal))
        setModalPhase('noteModal')
        return
      }
    }

    const optionsDataReady = practitionerDetailsReady && lengthOptions != null
    const optionsDataPending =
      shouldShowOptionsModal && !optionsDataReady && !optionsModalDataFailed
    if (optionsDataPending) return

    if (shouldShowOptionsModal && optionsDataReady) {
      setModalTriggerer('appointmentSelect')
      setModalPhase('optionsModal')
      return
    }

    setModalPhase('navigating')
  }, [
    modalPhase,
    appointmentInfo,
    node,
    nodeId,
    nodePending,
    isOhc,
    modals,
    modalsLoaded,
    shouldShowOptionsModal,
    practitionerDetailsReady,
    lengthOptions,
    optionsModalDataFailed,
    makeNoteConfig,
    setModalTriggerer,
  ])

  useEffect(() => {
    if (modalPhase !== 'navigating') return
    navigateToAppointment()
  }, [modalPhase, navigateToAppointment])

  const handleOptionsSelect = useCallback(
    (options: AppointmentOptions) => {
      setModalTriggerer(undefined)
      navigateToAppointment(options)
    },
    [setModalTriggerer, navigateToAppointment]
  )

  const handleNoteModalContinue = useCallback(() => {
    setNoteModalConfig(null)
    if (shouldShowOptionsModal && !optionsModalDataFailed) {
      setModalTriggerer('appointmentSelect')
      setModalPhase('optionsModal')
    } else {
      setModalPhase('navigating')
    }
  }, [shouldShowOptionsModal, optionsModalDataFailed, setModalTriggerer])

  const handleNoteModalClose = useCallback(() => {
    setNoteModalConfig(null)
    history.replace(buildBackPath())
  }, [history, buildBackPath])

  const handleOptionsClose = useCallback(() => {
    setModalTriggerer(undefined)
    history.replace(buildBackPath())
  }, [history, buildBackPath, setModalTriggerer])

  return {
    modalPhase,
    noteModalConfig,
    handleNoteModalContinue,
    handleNoteModalClose,
    handleOptionsSelect,
    handleOptionsClose,
  }
}
